import { createRouter, createWebHistory } from 'vue-router';
import Login from '../views/Login.vue'
import MainLayout from "../layouts/MainLayout.vue";
import EmployeeLayout from "../layouts/EmployeeLayout.vue";
import store from '../store'
import api from '../utils/api.js';
import ProjectList from "../views/ProjectList.vue";

const PdfPreview = () => import("../components/PdfPreview.vue");
const NotFound = () => import("../views/NotFound.vue");
const ChangePassword = () => import("../views/ChangePassword.vue");
const PasswordForgotten = () => import("../views/PasswordForgotten.vue");
const Account = () => import("../views/Account.vue");
const DepartmentManagement = () => import("../views/DepartmentManagement.vue");
const ResetPassword = () => import("../views/ResetPassword.vue");
const MicrosoftAuthSuccess = () => import("../views/MicrosoftAuthSuccess.vue");
const ProjectDetails = () => import("../views/ProjectDetails.vue");
const ProjectCreate = () => import("../views/ProjectCreate.vue");
const ProjectTaskDetails = () => import("../views/ProjectTaskDetails.vue");
const ProjectProgressMatrix = () => import("../views/ProjectProgressMatrix.vue");
const TimeApproval = () => import("../views/TimeApproval.vue");
const UserManagement = () => import("../views/UserManagement.vue");
const EmployeeDashboard = () => import("../views/EmployeeDashboard.vue");
const EmployeeSearch = () => import("../views/EmployeeSearch.vue");
const EmployeeAccount = () => import("../views/EmployeeAccount.vue");
const EmployeeProjectTaskDetails = () => import("../views/EmployeeProjectTaskDetails.vue");

const routes = [
    {
        path: '/',
        component: MainLayout,
        children: [
            {
                path: '',
                name: 'ProjectList',
                component: ProjectList,
                meta: { requiresAuth: true },
            },
            {
                path: '/projects/new',
                name: 'ProjectCreate',
                component: ProjectCreate,
                meta: { requiresAuth: true, fullWidth: true },
            },
            {
                path: '/projects/:reference',
                name: 'ProjectDetails',
                component: ProjectDetails,
                meta: { requiresAuth: true },
            },
            {
                path: '/projects/:reference/tasks/:taskReference',
                name: 'ProjectTaskDetails',
                component: ProjectTaskDetails,
                meta: { requiresAuth: true },
            },
            {
                path: '/projects/:reference/matrix',
                name: 'ProjectProgressMatrix',
                component: ProjectProgressMatrix,
                meta: { requiresAuth: true, fullWidth: true },
            },
            {
                path: '/uren-registratie',
                name: 'TimeApproval',
                component: TimeApproval,
                meta: { requiresAuth: true },
            },
            {
                path: '/account',
                name: 'Account',
                component: Account,
                meta: { requiresAuth: true },
            },
            {
                path: '/afdelingen',
                name: 'DepartmentManagement',
                component: DepartmentManagement,
                meta: { requiresAuth: true },
            },
            {
                path: '/gebruikers',
                name: 'UserManagement',
                component: UserManagement,
                meta: { requiresAuth: true },
            },
            {
                path: '/account/change-password',
                name: 'ChangePassword',
                component: ChangePassword,
                meta: { requiresAuth: true },
            },
            {
                path: '/:catchAll(.*)',
                name: 'NotFound',
                component: NotFound
            }
        ],
    },
    {
        path: '/werk',
        component: EmployeeLayout,
        children: [
            {
                path: '',
                name: 'EmployeeDashboard',
                component: EmployeeDashboard,
                meta: { requiresAuth: true, employee: true },
            },
            {
                path: 'zoeken',
                name: 'EmployeeSearch',
                component: EmployeeSearch,
                meta: { requiresAuth: true, employee: true },
            },
            {
                path: 'account',
                name: 'EmployeeAccount',
                component: EmployeeAccount,
                meta: { requiresAuth: true, employee: true },
            },
            {
                path: 'account/change-password',
                name: 'EmployeeChangePassword',
                component: ChangePassword,
                meta: { requiresAuth: true, employee: true },
            },
            {
                path: 'projects/:reference/tasks/:taskReference',
                name: 'EmployeeProjectTaskDetails',
                component: EmployeeProjectTaskDetails,
                meta: { requiresAuth: true, employee: true },
            },
        ],
    },
    {
        path: '/password-forgotten',
        name: 'PasswordForgotten',
        component: PasswordForgotten,
        meta: { guestOnly: true },
    },
    {
        path: '/reset-password',
        name: 'ResetPassword',
        component: ResetPassword,
        meta: { guestOnly: true },
    },
    {
        path: '/login',
        name: 'Login',
        component: Login,
        meta: { guestOnly: true },
    },
    {
        path: '/auth/microsoft/success',
        name: 'MicrosoftAuthSuccess',
        component: MicrosoftAuthSuccess,
    },
    {
        path: '/logout',
        name: 'Logout',
        beforeEnter: async () => {
            try {
                await api.post('/logout');
            } catch (error) {
                console.warn('Logout API request failed', error);
            }

            await store.dispatch('clearStore');

            globalThis.location.href = '/login'
        }
    },
    {
        path: '/pdf-preview',
        name: 'PdfPreview',
        component: PdfPreview,
    }
];

const passwordChangePaths = new Set(['/account/change-password', '/werk/account/change-password']);

function getUserRoles(user) {
    if (Array.isArray(user.roles)) {
        return user.roles;
    }

    return user.role ? [user.role] : [];
}

function isEmployeeOnlyUser(user) {
    const roles = getUserRoles(user);
    return !roles.includes('ROLE_ADMIN') && !roles.includes('ROLE_FOREMAN');
}

function employeeRouteRedirect(to) {
    if (to.name === 'ProjectTaskDetails') {
        return {
            name: 'EmployeeProjectTaskDetails',
            params: {
                reference: to.params.reference,
                taskReference: to.params.taskReference,
            },
            query: to.query,
        };
    }

    if (to.meta.requiresAuth && !to.meta.employee && to.path !== '/account/change-password') {
        return { name: 'EmployeeDashboard' };
    }

    return null;
}

function authenticatedRouteRedirect(to, user, isEmployeeOnly) {
    if (user.mustChangePassword && !passwordChangePaths.has(to.path)) {
        return isEmployeeOnly
            ? { name: 'EmployeeChangePassword' }
            : { path: '/account/change-password' };
    }

    if (isEmployeeOnly) {
        return employeeRouteRedirect(to);
    }

    if (to.meta.employee) {
        return { path: '/' };
    }

    return null;
}

function routeRedirect(to, token, user) {
    if (to.meta.requiresAuth && !token) {
        return { path: '/login', query: to.query };
    }

    if (!token) {
        return null;
    }

    const isEmployeeOnly = isEmployeeOnlyUser(user);
    const authenticatedRedirect = authenticatedRouteRedirect(to, user, isEmployeeOnly);
    if (authenticatedRedirect) {
        return authenticatedRedirect;
    }

    if (to.meta.guestOnly) {
        return { path: isEmployeeOnly ? '/werk' : '/' };
    }

    return null;
}

const router = createRouter({
    history: createWebHistory(),
    routes,
});

router.beforeEach((to, from, next) => {
    const token = store.getters['session/accessToken'];
    const user = store.getters.user || {};

    if (to.path === '/logout' || (token && passwordChangePaths.has(to.path))) {
        next();
        return;
    }

    const redirect = routeRedirect(to, token, user);
    if (redirect) {
        next(redirect);
        return;
    }

    next();
});

export default router;
